/**
 * DailyRebalancingPage — daily rebalance candidate engine + 5-year backtest +
 * portfolio upload + CUT/ADD/DROP/NEW recommendations.
 *
 * Pipeline visible to the user:
 *   1. Rising sub-sectors today (filtered from market overview)
 *   2. Top momentum candidates from those sub-sectors' ETF holdings
 *   3. Per-share upside table (analyst / predicts / technical / blended)
 *   4. 5-year backtest vs SPY / DIA / QQQ
 *   5. Portfolio upload (excel / csv / pdf / multi-image)
 *   6. Recommendations bucketed: DROP / CUT / KEEP / ADD / NEW
 */
import { useEffect, useState, useCallback, useMemo, Fragment } from "react";
import axios from "axios";
import {
  RefreshCw, TrendingUp, TrendingDown, Minus, Upload,
  ArrowUp, ArrowDown, AlertCircle, FileSpreadsheet, FileImage, FileText,
  Target, Activity, X, Lightbulb, ChevronDown, ChevronRight,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import AIAnalysisChat from "../components/AIAnalysisChat";
import PortfolioReturnsTable from "../components/PortfolioReturnsTable";
import PortfolioBenchmarkChart from "../components/PortfolioBenchmarkChart";
import EarlyWarningsCard from "../components/EarlyWarningsCard";
import { API } from "../config/api";

const CADENCES = [
  { key: "daily",     label: "Daily" },
  { key: "weekly",    label: "Weekly" },
  { key: "monthly",   label: "Monthly" },
  { key: "quarterly", label: "Quarterly" },
  { key: "yearly",    label: "Yearly" },
];

const fmtPct = (v, digits = 2) =>
  v === null || v === undefined ? "—" : `${v >= 0 ? "+" : ""}${Number(v).toFixed(digits)}%`;
const fmtPrice = (v) =>
  v === null || v === undefined ? "—" : `$${Number(v).toFixed(2)}`;

const pctClass = (v) => v == null ? "text-slate-400" : v >= 0 ? "text-emerald-300" : "text-rose-300";

/**
 * Renders a compact "options overlay" cell for picks that have a qualifying
 * liquid CALL contract attached. Designed for tight table cells — strike +
 * expiry on the top line, delta / IV / volume on the bottom, with a title
 * tooltip carrying the full contract terms for power users.
 */
function OptionsOverlayCell({ overlay }) {
  if (!overlay) {
    return <span className="text-[10px] text-slate-600 italic">no liquid call</span>;
  }
  const exp = overlay.expiration?.slice(5) || ""; // MM-DD
  const tooltip =
    `${overlay.expiration} · $${overlay.strike} CALL\n` +
    `Mid $${overlay.mid_price} (bid $${overlay.bid} / ask $${overlay.ask}, spread ${overlay.spread_pct}%)\n` +
    `Δ ${overlay.delta} · IV ${overlay.iv_pct}%\n` +
    `Vol ${overlay.volume?.toLocaleString()} · OI ${overlay.open_interest?.toLocaleString()}\n` +
    `Leverage ~${overlay.leverage_x}× · Breakeven $${overlay.breakeven_price} (${overlay.breakeven_move_pct >= 0 ? "+" : ""}${overlay.breakeven_move_pct}%)\n` +
    `1 contract ≈ $${overlay.contract_cost?.toLocaleString()}`;
  return (
    <div className="leading-tight" title={tooltip}>
      <div className="font-mono text-emerald-300 text-[11px] font-bold">
        ${overlay.strike}c {exp}
        <span className="ml-1 text-slate-400 font-normal text-[10px]">${overlay.mid_price}</span>
      </div>
      <div className="text-[9px] text-slate-500 font-mono">
        Δ{overlay.delta} · IV{overlay.iv_pct}% · V{(overlay.volume || 0).toLocaleString()}
        {overlay.leverage_x ? ` · ${overlay.leverage_x}×` : ""}
      </div>
    </div>
  );
}

// ────────────────────────── Sections ──────────────────────────

function RisingSubSectorsSection({ data, cadence }) {
  const rows = data?.rising_sub_sectors || [];
  const label = data?.cadence_label || cadence;
  return (
    <Card className="bg-slate-900/40 border-slate-800" data-testid="dr-rising-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-white flex items-center justify-between">
          <span className="flex items-center gap-1.5"><TrendingUp className="w-4 h-4 text-emerald-400" /> Rising sub-sectors ({label.toLowerCase()})</span>
          <span className="text-[10px] font-normal text-slate-500">{rows.length} rising</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {!data ? (
          <div className="p-3 space-y-1"><Skeleton className="h-6 w-full" /><Skeleton className="h-6 w-full" /><Skeleton className="h-6 w-full" /></div>
        ) : rows.length === 0 ? (
          <div className="p-4 text-center text-[11px] text-slate-500">No sub-sectors with positive {label.toLowerCase()} returns right now.</div>
        ) : (
          <ul className="max-h-72 overflow-y-auto" data-testid="dr-rising-list">
            {rows.slice(0, 15).map((s, i) => (
              <li key={`${s.sub_sector}-${i}`} className="flex items-center justify-between px-3 py-1.5 border-b border-slate-900 text-[11px] hover:bg-slate-800/30">
                <span className="flex items-center gap-2 truncate">
                  <span className="text-slate-500 font-mono w-5">{i + 1}</span>
                  <a href={`/stock?ticker=${s.sub_sector_etf}`} className="font-mono font-bold text-cyan-300 hover:underline w-12">{s.sub_sector_etf}</a>
                  <span className="text-slate-300 truncate">{s.sub_sector}</span>
                  <span className="text-slate-600">·</span>
                  <span className="text-slate-500 truncate">{s.sector}</span>
                </span>
                <span className="text-emerald-300 font-mono font-semibold w-16 text-right">{fmtPct(s.period_return)}</span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function CandidatesSection({ data, onSelect, selectedTicker }) {
  const candidates = data?.candidates || [];
  const filteredOut = data?.filtered_out || [];
  const etfOnly = data?.etf_only_subsectors || [];
  const target = data?.target_return_pct;
  const cadence = data?.cadence || "daily";
  const projHeader = cadence === "daily" ? "Proj. close" : `Proj. ${cadence}`;
  return (
    <Card className="bg-slate-900/40 border-slate-800" data-testid="dr-candidates-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-white flex items-center justify-between">
          <span className="flex items-center gap-1.5"><Target className="w-4 h-4 text-amber-400" /> Top momentum candidates
            {target != null && (
              <span className="ml-1 text-[10px] font-normal text-amber-300 bg-amber-500/10 border border-amber-500/40 rounded px-1.5 py-0.5">
                ≥ {target}% target
              </span>
            )}
          </span>
          <span className="text-[10px] font-normal text-slate-500">click a row to see upside</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {!data ? (
          <div className="p-3 space-y-1">{Array(5).fill(0).map((_, i) => <Skeleton key={i} className="h-7 w-full" />)}</div>
        ) : candidates.length === 0 ? (
          <div className="p-4 text-center text-[11px] text-slate-500 space-y-2" data-testid="dr-candidates-empty">
            {target != null ? (
              <>
                <div className="text-amber-300">
                  No stocks currently project ≥ <span className="font-mono">{target}%</span> over the {cadence} horizon.
                </div>
                <div className="text-slate-400">Try lowering the target, extending the cadence, or disabling the target filter.</div>
                {filteredOut.length > 0 && (
                  <div className="pt-2 border-t border-slate-800 mt-2 text-left">
                    <div className="text-[10px] uppercase text-slate-500 mb-1.5 px-3">Closest near-misses</div>
                    <table className="w-full text-[11px]">
                      <tbody>
                        {filteredOut.slice(0, 6).map((c) => (
                          <tr key={c.ticker} className="border-b border-slate-900 hover:bg-slate-800/30 cursor-pointer"
                              onClick={() => onSelect(c.ticker)}>
                            <td className="py-1 px-3 font-mono font-bold text-cyan-300">{c.ticker}</td>
                            <td className="py-1 px-3 text-slate-400 truncate max-w-[120px]">{c.company_name || "—"}</td>
                            <td className={`py-1 px-3 text-right font-mono ${pctClass(c.projected_return_pct)}`}>
                              {fmtPct(c.projected_return_pct)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            ) : (
              <>No real-holdings candidates available — see ETF-only options below.</>
            )}
          </div>
        ) : (
          <table className="w-full text-[11px]" data-testid="dr-candidates-table">
            <thead className="text-[10px] uppercase text-slate-500 border-b border-slate-800">
              <tr>
                <th className="text-left py-1.5 px-3">Ticker</th>
                <th className="text-left py-1.5 px-3">Company</th>
                <th className="text-left py-1.5 px-3">Sub-sector</th>
                <th className="text-right py-1.5 px-3">Today</th>
                <th className="text-right py-1.5 px-3 text-amber-300">{projHeader}</th>
                <th className="text-right py-1.5 px-3">Price</th>
                <th className="text-right py-1.5 px-3">RSI</th>
                <th className="text-left py-1.5 px-3">via ETF</th>
              </tr>
            </thead>
            <tbody>
              {candidates.slice(0, 20).map((c) => (
                <tr key={c.ticker}
                    className={`border-b border-slate-900 cursor-pointer hover:bg-slate-800/30 ${selectedTicker === c.ticker ? "bg-cyan-500/10" : ""}`}
                    onClick={() => onSelect(c.ticker)}
                    data-testid={`dr-cand-${c.ticker}`}>
                  <td className="py-1.5 px-3 font-mono font-bold text-cyan-300">{c.ticker}</td>
                  <td className="py-1.5 px-3 text-slate-300 truncate max-w-[140px]">{c.company_name || "—"}</td>
                  <td className="py-1.5 px-3 text-slate-300 truncate max-w-[160px]">{c.sub_sector}</td>
                  <td className={`py-1.5 px-3 text-right font-mono font-semibold ${pctClass(c.change_pct_today)}`}>{fmtPct(c.change_pct_today)}</td>
                  <td className={`py-1.5 px-3 text-right font-mono font-bold ${pctClass(c.projected_return_pct)}`}
                      data-testid={`dr-cand-${c.ticker}-projected`}>
                    {fmtPct(c.projected_return_pct)}
                  </td>
                  <td className="py-1.5 px-3 text-right font-mono text-slate-300">{fmtPrice(c.current_price)}</td>
                  <td className="py-1.5 px-3 text-right font-mono text-slate-400">{c.mini_rsi ?? "—"}</td>
                  <td className="py-1.5 px-3 text-slate-500 font-mono">{c.resolved_etf}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {etfOnly.length > 0 && (
          <div className="border-t border-slate-800 p-3 bg-slate-900/30">
            <div className="text-[10px] uppercase text-slate-500 mb-1.5">ETF-only sub-sectors (no constituent-level data — trade the ETF)</div>
            <div className="flex flex-wrap gap-1.5">
              {etfOnly.map((e) => (
                <a key={e.sub_sector_etf} href={`/stock?ticker=${e.sub_sector_etf}`}
                   className="text-[10px] px-2 py-1 rounded border border-slate-700 bg-slate-900 hover:bg-slate-800 text-slate-300">
                  <span className="font-mono text-cyan-300">{e.sub_sector_etf}</span>
                  <span className="mx-1 text-slate-600">·</span>
                  <span className="text-emerald-300">{fmtPct(e.period_return)}</span>
                  <span className="mx-1 text-slate-600">·</span>
                  <span>{e.sub_sector}</span>
                </a>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function UpsideSection({ ticker }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!ticker) { setData(null); return; }
    setLoading(true);
    axios.get(`${API}/daily-rebalancing/upside/${ticker}`, { timeout: 30000 })
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [ticker]);

  if (!ticker) return null;
  const projections = data?.projections || {};
  const order = ["analyst", "predicts", "technical", "blended"];
  const labels = {
    analyst: "Analyst Consensus",
    predicts: "StockPimp Predicts",
    technical: "Technical (60d regression)",
    blended: "Blended (average)",
  };

  return (
    <Card className="bg-slate-900/40 border-slate-800" data-testid="dr-upside-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-white flex items-center justify-between">
          <span className="flex items-center gap-1.5">
            <ArrowUp className="w-4 h-4 text-emerald-400" />
            Per-share upside · <a href={`/stock?ticker=${ticker}`} className="font-mono text-cyan-300 hover:underline">{ticker}</a>
          </span>
          {data?.current_price && (
            <span className="text-[11px] font-normal text-slate-400">current {fmtPrice(data.current_price)}</span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {loading || !data ? (
          <div className="p-3 space-y-1">{Array(4).fill(0).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}</div>
        ) : (
          <table className="w-full text-[11px]">
            <thead className="text-[10px] uppercase text-slate-500 border-b border-slate-800">
              <tr>
                <th className="text-left py-1.5 px-3">Source</th>
                <th className="text-right py-1.5 px-3">Projected price</th>
                <th className="text-right py-1.5 px-3">$ Upside / share</th>
                <th className="text-right py-1.5 px-3">% Upside</th>
                <th className="text-left py-1.5 px-3">Detail</th>
              </tr>
            </thead>
            <tbody>
              {order.map((k) => {
                const p = projections[k] || {};
                const blended = k === "blended";
                return (
                  <tr key={k} className={`border-b border-slate-900 ${blended ? "bg-cyan-500/5 font-semibold" : ""}`} data-testid={`dr-upside-${k}`}>
                    <td className="py-1.5 px-3 text-slate-200">{labels[k]}</td>
                    <td className="py-1.5 px-3 text-right font-mono text-slate-200">{fmtPrice(p.projected_price)}</td>
                    <td className={`py-1.5 px-3 text-right font-mono ${pctClass(p.per_share_upside)}`}>
                      {p.per_share_upside == null ? "—" : `${p.per_share_upside >= 0 ? "+" : ""}$${Number(p.per_share_upside).toFixed(2)}`}
                    </td>
                    <td className={`py-1.5 px-3 text-right font-mono ${pctClass(p.upside_pct)}`}>{fmtPct(p.upside_pct)}</td>
                    <td className="py-1.5 px-3 text-slate-500 text-[10px] truncate max-w-[300px]">{p.source_detail}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}

const VERDICT_COLOR = {
  emerald: "border-emerald-500/40 bg-emerald-500/10 text-emerald-200",
  amber: "border-amber-500/40 bg-amber-500/10 text-amber-200",
  rose: "border-rose-500/40 bg-rose-500/10 text-rose-200",
};

function BacktestSection({ cadence, enabled = true }) {
  const [bt, setBt] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled) { setBt(null); setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    setBt(null);
    axios.get(`${API}/daily-rebalancing/backtest?years=5&cadence=${cadence}`, { timeout: 240000 })
      .then(r => { if (!cancelled) setBt(r.data); })
      .catch(() => { if (!cancelled) setBt(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [cadence, enabled]);

  // Build merged chart data from curves
  const chartData = (() => {
    if (!bt?.curves) return [];
    const series = Object.entries(bt.curves);
    const keyMap = new Map();
    for (const [name, pts] of series) {
      for (const p of pts) {
        const e = keyMap.get(p.date) || { date: p.date };
        e[name] = p.cum_return_pct;
        keyMap.set(p.date, e);
      }
    }
    return Array.from(keyMap.values()).sort((a, b) => a.date.localeCompare(b.date));
  })();

  const colorMap = { STRATEGY: "#a78bfa", SPY: "#22d3ee", DIA: "#f59e0b", QQQ: "#10b981" };

  return (
    <Card className="bg-slate-900/40 border-slate-800" data-testid="dr-backtest-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-white flex items-center justify-between">
          <span className="flex items-center gap-1.5"><Activity className="w-4 h-4 text-purple-400" /> 5-Year Backtest — {bt?.cadence_label || cadence} rotation vs benchmarks</span>
          {bt?.verdict_color && (
            <span className={`text-[10px] border rounded px-2 py-0.5 ${VERDICT_COLOR[bt.verdict_color]}`} data-testid="dr-bt-verdict">
              {bt.verdict}
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading || !bt ? (
          <Skeleton className="h-64 w-full" />
        ) : bt.error ? (
          <div className="text-[11px] text-rose-300 p-4">{bt.error}</div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
              {bt.summary && Object.entries(bt.summary).map(([k, v]) => (
                <div key={k} className={`rounded border p-2 ${k === "STRATEGY" ? "border-purple-500/40 bg-purple-500/10" : "border-slate-700 bg-slate-900/40"}`} data-testid={`dr-bt-stat-${k}`}>
                  <div className="text-[10px] uppercase text-slate-400">{k}</div>
                  <div className={`text-base font-bold tabular-nums ${pctClass(v.total_return_pct)}`}>{fmtPct(v.total_return_pct, 1)}</div>
                  <div className="text-[10px] text-slate-500">
                    ann {fmtPct(v.annualized_pct, 1)} · sharpe {v.sharpe_approx} · mdd {fmtPct(-Math.abs(v.max_drawdown_pct), 1)}
                  </div>
                </div>
              ))}
            </div>
            <div className="h-64" data-testid="dr-bt-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 4, right: 10, bottom: 4, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="date" tick={{ fontSize: 9, fill: "#94a3b8" }} interval={Math.max(1, Math.floor(chartData.length / 8))} />
                  <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} tickFormatter={(v) => `${v.toFixed(0)}%`} />
                  <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", fontSize: 11 }}
                           formatter={(v) => `${Number(v).toFixed(1)}%`} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {Object.keys(colorMap).map((k) => (
                    <Line key={k} type="monotone" dataKey={k} stroke={colorMap[k]} dot={false} strokeWidth={k === "STRATEGY" ? 2.5 : 1.5} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
            <p className="text-[10px] text-slate-500 italic mt-2 leading-snug">
              <strong>Methodology:</strong> {bt.methodology}
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}

const ACTION_STYLE = {
  DROP: { color: "text-rose-300",   bg: "border-rose-500/40 bg-rose-500/10",   icon: TrendingDown, desc: "Hard negative move — consider exit." },
  CUT:  { color: "text-amber-300",  bg: "border-amber-500/40 bg-amber-500/10", icon: ArrowDown,    desc: "Flat-to-down — reduce exposure." },
  KEEP: { color: "text-slate-300",  bg: "border-slate-700 bg-slate-900/40",    icon: Minus,        desc: "Mild positive — hold." },
  ADD:  { color: "text-emerald-300",bg: "border-emerald-500/40 bg-emerald-500/10", icon: ArrowUp,  desc: "Strong momentum + in rising sub-sector." },
  NEW:  { color: "text-cyan-300",   bg: "border-cyan-500/40 bg-cyan-500/10",   icon: TrendingUp,   desc: "Top momentum candidate — not currently held." },
};

function PortfolioUploadSection({ onParsed, onRecommend, onRecoveryPicks, onLockInGains, holdings, parseInfo, recsLoading, recsElapsed, recoveryLoading, lockInLoading, lossPreview }) {
  const [busy, setBusy] = useState(false);
  const [busyElapsed, setBusyElapsed] = useState(0);
  const [err, setErr] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    if (!busy) { setBusyElapsed(0); return; }
    const t0 = Date.now();
    const id = setInterval(() => setBusyElapsed(Math.floor((Date.now() - t0) / 1000)), 250);
    return () => clearInterval(id);
  }, [busy]);

  const upload = async (files) => {
    setErr(null);
    if (!files || files.length === 0) return;
    setBusy(true);
    // Auto-retry once on 503 (server busy) — gives the backend a few seconds
    // to drain its in-flight queue when the page just kicked off a big load.
    const postWithRetry = async () => {
      const fd = new FormData();
      [...files].forEach(f => fd.append("files", f));
      try {
        return await axios.post(`${API}/daily-rebalancing/upload`, fd, { timeout: 180000 });
      } catch (e) {
        if (e?.response?.status === 503) {
          await new Promise(res => setTimeout(res, 4000));
          const fd2 = new FormData();
          [...files].forEach(f => fd2.append("files", f));
          return await axios.post(`${API}/daily-rebalancing/upload`, fd2, { timeout: 180000 });
        }
        throw e;
      }
    };
    try {
      const r = await postWithRetry();
      onParsed(r.data.holdings || [], {
        kind: r.data.kind,
        method: r.data.method,
        warning: r.data.warning || (r.data.file_errors?.length ? `Some files could not be parsed: ${r.data.file_errors.join(" | ")}` : null),
        n_holdings: r.data.n_holdings,
        n_dropped: r.data.n_dropped,
        dropped_holdings: r.data.dropped_holdings,
        reconciliation: r.data.reconciliation,
        statement_total: r.data.statement_total,
        cash_total: r.data.cash_total,
        parsed_market_total: r.data.parsed_market_total,
      });
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Upload failed");
      onParsed([], { kind: "error", method: null, warning: null });
    } finally {
      setBusy(false);
    }
  };

  const showParseWarning = parseInfo?.warning || (parseInfo && parseInfo.n_holdings === 0 && parseInfo.kind !== "error");

  // Sortable parsed-holdings table (click header: high→low, again: low→high)
  const [tblSortKey, setTblSortKey] = useState(null);
  const [tblSortDir, setTblSortDir] = useState("desc");
  const onTblSort = (key) => {
    if (tblSortKey === key) {
      if (tblSortDir === "desc") setTblSortDir("asc");
      else { setTblSortKey(null); setTblSortDir("desc"); }
    } else {
      setTblSortKey(key); setTblSortDir("desc");
    }
  };
  const numOrNull = (v) => (v === null || v === undefined || v === "" ? null : Number(v));
  const TBL_COLS = [
    { key: "ticker", label: "Symbol", align: "left", get: h => h.ticker || "" },
    { key: "desc", label: "Description", align: "left", get: h => h._statement_description || h.company_name || "" },
    { key: "qty", label: "Qty", get: h => numOrNull(h.shares) },
    { key: "pchg_d", label: "Price chg $", get: h => numOrNull(h._statement_price_chg_dollar) },
    { key: "pchg_p", label: "Price chg %", get: h => numOrNull(h._statement_price_chg_pct) },
    { key: "mkt", label: "Mkt val", get: h => numOrNull(h._statement_mkt_val ?? h.market_value) },
    { key: "dchg_d", label: "Day chg $", get: h => numOrNull(h._statement_day_chg_dollar) },
    { key: "dchg_p", label: "Day chg %", get: h => numOrNull(h._statement_day_chg_pct) },
    { key: "cost", label: "Cost basis", get: h => numOrNull(h._total_cost ?? (h.cost_basis && h.shares ? Number(h.cost_basis) * Number(h.shares) : null)) },
    { key: "gain", label: "Gain/Loss", get: h => numOrNull(h._statement_gain_loss_pct) },
  ];
  const sortedHoldings = useMemo(() => {
    if (!tblSortKey) return holdings || [];
    const col = TBL_COLS.find(c => c.key === tblSortKey);
    if (!col) return holdings || [];
    return [...(holdings || [])].sort((a, b) => {
      const av = col.get(a);
      const bv = col.get(b);
      if (typeof av === "string" || typeof bv === "string") {
        return tblSortDir === "desc"
          ? String(bv).localeCompare(String(av))
          : String(av).localeCompare(String(bv));
      }
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return tblSortDir === "desc" ? bv - av : av - bv;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holdings, tblSortKey, tblSortDir]);

  return (
    <Card className="bg-slate-900/40 border-slate-800" data-testid="dr-upload-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-white flex items-center gap-1.5">
          <Upload className="w-4 h-4 text-cyan-400" />
          Upload your portfolio
          <span className="text-[10px] font-normal text-slate-500">(up to 20 Excel/CSV + 20 PDFs + 20 screenshots per upload — holdings are merged)</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <label
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); upload(e.dataTransfer.files); }}
          className={`flex flex-col items-center justify-center p-6 border-2 border-dashed rounded cursor-pointer transition-colors ${
            dragOver ? "border-cyan-400 bg-cyan-500/5" : "border-slate-700 hover:border-slate-500 bg-slate-900/30"
          }`}
          data-testid="dr-upload-drop"
        >
          <input
            type="file" multiple
            accept=".xlsx,.xls,.csv,.pdf,.png,.jpg,.jpeg,.webp,.heic,.heif,image/*"
            onChange={(e) => upload(e.target.files)}
            className="hidden"
            data-testid="dr-upload-input"
          />
          {busy ? (
            <div className="flex flex-col items-center gap-1 text-cyan-300 text-xs">
              <div className="flex items-center gap-2">
                <RefreshCw className="w-4 h-4 animate-spin" /> Parsing…
                <span className="font-mono text-slate-400 text-[11px]">{busyElapsed}s</span>
              </div>
              <span className="text-[10px] text-slate-500 mt-1">
                PDFs may take 30–60s if image OCR fallback is needed.
              </span>
            </div>
          ) : (
            <>
              <div className="flex gap-4 text-slate-500 mb-2">
                <FileSpreadsheet className="w-7 h-7" />
                <FileText className="w-7 h-7" />
                <FileImage className="w-7 h-7" />
              </div>
              <div className="text-[11px] text-slate-300 font-semibold">Drop Excel · CSV · PDF · or phone screenshots</div>
              <div className="text-[10px] text-slate-500 mt-1">or click to browse — pick one method, system parses automatically</div>
            </>
          )}
        </label>
        {err && (
          <div className="mt-2 flex items-start gap-1.5 text-[11px] text-rose-300 bg-rose-500/10 border border-rose-500/40 rounded p-2"
               data-testid="dr-upload-error">
            <AlertCircle className="w-3 h-3 mt-0.5" /> <span>{err}</span>
          </div>
        )}
        {showParseWarning && (
          <div className="mt-2 flex items-start gap-1.5 text-[11px] text-amber-300 bg-amber-500/10 border border-amber-500/40 rounded p-2"
               data-testid="dr-parse-warning">
            <AlertCircle className="w-3 h-3 mt-0.5" />
            <span>
              {parseInfo?.warning || "No holdings were extracted from the upload."}
              {" "}Try a CSV export (most reliable) or upload phone screenshots of each page.
            </span>
          </div>
        )}
        {holdings?.length > 0 && (
          <div className="mt-3 space-y-2">
            {parseInfo?.reconciliation && (
              <div
                className={`text-[11px] rounded border p-2.5 ${
                  parseInfo.reconciliation.verdict === "match"
                    ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
                    : parseInfo.reconciliation.verdict === "close"
                    ? "border-amber-500/40 bg-amber-500/10 text-amber-200"
                    : "border-rose-500/40 bg-rose-500/10 text-rose-200"
                }`}
                data-testid="dr-reconciliation-banner"
              >
                <div className="font-semibold mb-1">
                  {parseInfo.reconciliation.verdict === "match" && (
                    parseInfo.reconciliation.cash_total
                      ? "✓ Reconciled with statement total — the difference is your cash position"
                      : "✓ Reconciled with statement total"
                  )}
                  {parseInfo.reconciliation.verdict === "close" && "≈ Close to statement total"}
                  {parseInfo.reconciliation.verdict === "mismatch" && "⚠ Mismatch with statement total — sanity check below"}
                  {parseInfo.reconciliation.source === "broker_per_row" && (
                    <span className="ml-2 text-[9px] font-mono opacity-60 uppercase">broker per-row × row-sum</span>
                  )}
                </div>
                <div className={`grid ${parseInfo.reconciliation.cash_total ? "grid-cols-4" : "grid-cols-3"} gap-2 text-[11px] font-mono`}>
                  <div>
                    <div className="opacity-60 text-[10px] uppercase">Statement total</div>
                    <div className="text-sm">${Number(parseInfo.reconciliation.statement_total).toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
                  </div>
                  <div>
                    <div className="opacity-60 text-[10px] uppercase">
                      {parseInfo.reconciliation.source === "broker_per_row" ? "Parsed (sum of rows)" : "Parsed market value"}
                    </div>
                    <div className="text-sm">${Number(parseInfo.reconciliation.parsed_market_total).toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
                  </div>
                  {parseInfo.reconciliation.cash_total ? (
                    <div data-testid="dr-cash-position">
                      <div className="opacity-60 text-[10px] uppercase">Cash position</div>
                      <div className="text-sm text-emerald-300">
                        ${Number(parseInfo.reconciliation.cash_total).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                      </div>
                    </div>
                  ) : null}
                  <div>
                    <div className="opacity-60 text-[10px] uppercase">
                      {parseInfo.reconciliation.cash_total ? "Δ Diff (rows + cash)" : "Δ Difference"}
                    </div>
                    <div className="text-sm">
                      {parseInfo.reconciliation.difference >= 0 ? "+" : ""}${Number(parseInfo.reconciliation.difference).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                      <span className="ml-1 opacity-70 text-[10px]">({parseInfo.reconciliation.difference_pct}%)</span>
                    </div>
                  </div>
                </div>
                {parseInfo.reconciliation.verdict === "mismatch" && (
                  <div className="mt-2 text-[10px] opacity-80 leading-snug">
                    The parsed market value differs by more than 5% from your statement.
                    Common causes: the parser grabbed text noise as a fake ticker, or cash/options/mutual funds aren't included in the parsed list (and that's fine).
                    Cross-check the company names below and remove any holding that's not really yours before generating recommendations.
                  </div>
                )}
              </div>
            )}
            {parseInfo?.statement_total && !parseInfo?.reconciliation && (
              <div className="text-[11px] text-slate-400 bg-slate-900/40 border border-slate-700 rounded p-2">
                Statement total detected: <span className="font-mono text-slate-200">${Number(parseInfo.statement_total).toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
              </div>
            )}
            {parseInfo?.n_dropped > 0 && (
              <div className="text-[11px] text-amber-300 bg-amber-500/10 border border-amber-500/40 rounded p-2 flex items-start gap-2"
                   data-testid="dr-dropped-banner">
                <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
                <div>
                  <span className="font-semibold">Removed {parseInfo.n_dropped} row{parseInfo.n_dropped === 1 ? "" : "s"} — share count couldn't be identified</span>
                  {" "}so they can't be sized or included in the market-value balance.
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {(parseInfo.dropped_holdings || []).slice(0, 30).map((d, i) => (
                      <span key={i} className="px-1.5 py-0.5 rounded bg-amber-500/20 font-mono text-[10px]">
                        {d.ticker}{d.company_name ? ` · ${d.company_name}` : ""}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-[11px] text-slate-300 font-semibold flex items-center gap-2">
                Parsed {holdings.length} holding{holdings.length === 1 ? "" : "s"}
                {parseInfo?.method && (
                  <span className="text-[10px] font-normal text-slate-500 border border-slate-700 rounded px-1.5 py-0.5">
                    via {parseInfo.method}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={onRecommend}
                disabled={recsLoading}
                data-testid="dr-recommend-btn"
                className="text-[11px] px-3 py-1.5 rounded bg-cyan-600/30 border border-cyan-500 text-cyan-200 hover:bg-cyan-600/50 disabled:opacity-50 flex items-center gap-1.5"
              >
                {recsLoading ? (
                  <>
                    <RefreshCw className="w-3 h-3 animate-spin" /> Analyzing…
                    <span className="font-mono text-cyan-100 text-[10px]">{recsElapsed}s</span>
                  </>
                ) : (
                  "Get recommendations →"
                )}
              </button>
              {/* Loss Recovery — auto-shows the loss preview as a badge */}
              <button
                onClick={onRecoveryPicks}
                disabled={recoveryLoading}
                data-testid="dr-recovery-btn"
                title="Find BUY picks projected to offset today's loss"
                className={`text-[11px] px-3 py-1.5 rounded border flex items-center gap-1.5 disabled:opacity-50 ${
                  lossPreview && lossPreview.drag > 0
                    ? "bg-rose-600/25 border-rose-500 text-rose-100 hover:bg-rose-600/40 shadow-[0_0_10px_rgba(244,63,94,0.25)]"
                    : "bg-amber-600/20 border-amber-500 text-amber-200 hover:bg-amber-600/40"
                }`}
              >
                {recoveryLoading ? (
                  <><RefreshCw className="w-3 h-3 animate-spin" /> Finding picks…</>
                ) : (
                  <>
                    <TrendingDown className="w-3 h-3" />
                    {lossPreview && lossPreview.drag > 0
                      ? <>Cover ${lossPreview.drag.toLocaleString(undefined,{maximumFractionDigits:0})} drag →</>
                      : <>Loss recovery picks →</>}
                  </>
                )}
              </button>
              {/* Lock in gains — shown when net day move is positive */}
              <button
                onClick={onLockInGains}
                disabled={lockInLoading}
                data-testid="dr-lockin-btn"
                title="Find positions to take profit on before they fade"
                className={`text-[11px] px-3 py-1.5 rounded border flex items-center gap-1.5 disabled:opacity-50 ${
                  lossPreview && lossPreview.net > 0
                    ? "bg-emerald-600/25 border-emerald-500 text-emerald-100 hover:bg-emerald-600/40 shadow-[0_0_10px_rgba(16,185,129,0.25)]"
                    : "bg-slate-700/30 border-slate-600 text-slate-300 hover:bg-slate-700/50"
                }`}
              >
                {lockInLoading ? (
                  <><RefreshCw className="w-3 h-3 animate-spin" /> Scanning…</>
                ) : (
                  <>
                    <TrendingUp className="w-3 h-3" />
                    {lossPreview && lossPreview.net > 0
                      ? <>Lock in ${Math.abs(lossPreview.net).toLocaleString(undefined,{maximumFractionDigits:0})} gain →</>
                      : <>Lock in gains →</>}
                  </>
                )}
              </button>
              </div>
            </div>
            <div className="overflow-x-auto border border-slate-800 rounded">
              <table className="w-full text-[11px]" data-testid="dr-parsed-table">
                <thead className="text-[10px] uppercase text-slate-500 bg-slate-900/50 border-b border-slate-800">
                  <tr>
                    {TBL_COLS.map(c => (
                      <th
                        key={c.key}
                        onClick={() => onTblSort(c.key)}
                        data-testid={`dr-parsed-sort-${c.key}`}
                        title={`Sort by ${c.label} (high → low, click again for low → high)`}
                        className={`${c.align === "left" ? "text-left" : "text-right"} py-1.5 px-3 cursor-pointer select-none hover:text-white transition-colors ${
                          tblSortKey === c.key ? "text-cyan-300 font-bold" : ""
                        }`}
                      >
                        {c.label}{tblSortKey === c.key ? (tblSortDir === "desc" ? " ▼" : " ▲") : ""}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedHoldings.map((h, i) => {
                    const fmtSigned$ = (v) => {
                      if (v === null || v === undefined || v === "") return "—";
                      const n = Number(v);
                      const sign = n >= 0 ? "+" : "-";
                      return `${sign}$${Math.abs(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
                    };
                    const fmtSignedPct = (v) => {
                      if (v === null || v === undefined || v === "") return "—";
                      const n = Number(v);
                      return `${n >= 0 ? "+" : ""}${n.toFixed(2)}%`;
                    };
                    const signClass = (v) => {
                      if (v === null || v === undefined || v === "") return "text-slate-300";
                      return Number(v) >= 0 ? "text-emerald-400" : "text-rose-400";
                    };
                    // Prefer broker description from statement; fall back to live-API name.
                    const desc = h._statement_description || h.company_name;
                    // Broker statement market value (preferred for table); fall back to live.
                    const mktVal = h._statement_mkt_val ?? h.market_value;
                    // Broker total cost basis; fall back to per-share × qty.
                    const costBasis = h._total_cost
                      ?? (h.cost_basis && h.shares ? Number(h.cost_basis) * Number(h.shares) : null);
                    return (
                      <tr key={`${h.ticker}-${i}`}
                          className={`border-b border-slate-900 ${h.name_unresolved ? "bg-amber-500/5" : ""}`}
                          data-testid={`dr-parsed-row-${h.ticker}`}>
                        <td className="py-1 px-3 font-mono font-bold text-cyan-300">
                          <a
                            href={`/stock?ticker=${h.ticker}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:underline hover:text-cyan-200"
                            data-testid={`dr-parsed-link-${h.ticker}`}
                          >
                            {h.ticker}
                          </a>
                        </td>
                        <td className="py-1 px-3 text-slate-300 truncate max-w-[180px]">
                          {desc || <span className="text-amber-300 italic">Unrecognized ticker</span>}
                        </td>
                        <td className="py-1 px-3 text-right font-mono text-slate-300">{h.shares ?? "—"}</td>
                        <td className={`py-1 px-3 text-right font-mono ${signClass(h._statement_price_chg_dollar)}`}>
                          {fmtSigned$(h._statement_price_chg_dollar)}
                        </td>
                        <td className={`py-1 px-3 text-right font-mono ${signClass(h._statement_price_chg_pct)}`}>
                          {fmtSignedPct(h._statement_price_chg_pct)}
                        </td>
                        <td className="py-1 px-3 text-right font-mono text-slate-300">
                          {mktVal ? `$${Number(mktVal).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : "—"}
                        </td>
                        <td className={`py-1 px-3 text-right font-mono ${signClass(h._statement_day_chg_dollar)}`}>
                          {fmtSigned$(h._statement_day_chg_dollar)}
                        </td>
                        <td className={`py-1 px-3 text-right font-mono ${signClass(h._statement_day_chg_pct)}`}>
                          {fmtSignedPct(h._statement_day_chg_pct)}
                        </td>
                        <td className="py-1 px-3 text-right font-mono text-slate-300">
                          {costBasis ? `$${Number(costBasis).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : "—"}
                        </td>
                        <td className={`py-1 px-3 text-right font-mono ${signClass(h._statement_gain_loss_pct)}`}>
                          {fmtSignedPct(h._statement_gain_loss_pct)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* Loading skeleton shown DIRECTLY below the upload card while recs compute. */
function RecsLoadingSkeleton({ elapsed }) {
  return (
    <Card className="bg-slate-900/40 border-slate-800" data-testid="dr-recs-loading">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-white flex items-center justify-between">
          <span className="flex items-center gap-2">
            <RefreshCw className="w-4 h-4 text-cyan-400 animate-spin" />
            Analyzing your portfolio…
          </span>
          <span className="text-[10px] font-normal font-mono text-slate-400">{elapsed}s</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-[11px] text-slate-400 mb-3">
          Pricing each holding, cross-referencing with today's rising sub-sectors, computing position weights, and sizing each recommendation.
        </div>
        <div className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
        <div className="text-[10px] text-slate-500 italic mt-3">
          Typically completes in 8–20s depending on portfolio size.
        </div>
      </CardContent>
    </Card>
  );
}

function LockInGainsSection({ data, loading, error }) {
  if (loading) {
    return (
      <Card className="bg-slate-900/40 border-emerald-500/40" data-testid="dr-lockin-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-emerald-200 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-emerald-400 animate-pulse" /> Lock-In Gains Mode
          </CardTitle>
        </CardHeader>
        <CardContent className="p-3 space-y-2">
          {Array(4).fill(0).map((_, i) => <Skeleton key={i} className="h-9 w-full" />)}
          <div className="text-[11px] text-slate-400">Scanning your winners for fade risk…</div>
        </CardContent>
      </Card>
    );
  }
  if (error) {
    return (
      <Card className="bg-slate-900/40 border-emerald-500/40" data-testid="dr-lockin-card">
        <CardContent className="p-4 flex items-start gap-2 text-rose-300 text-[12px]">
          <AlertCircle className="w-4 h-4 mt-0.5" />
          <div><div className="font-semibold">Couldn't compute lock-in picks</div><div className="text-[11px] mt-1">{error}</div></div>
        </CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const fmt$ = (v) => v == null ? "—" : `${v < 0 ? "-" : ""}$${Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  const actionLabel = { TAKE_PROFIT_FULL: "Sell 100%", TAKE_PROFIT_HALF: "Sell 50%", TRIM: "Trim 25%" };
  const actionColor = { TAKE_PROFIT_FULL: "text-rose-300", TAKE_PROFIT_HALF: "text-amber-300", TRIM: "text-emerald-300" };

  return (
    <Card className="bg-gradient-to-br from-slate-900/60 to-emerald-950/30 border-emerald-500/40" data-testid="dr-lockin-card">
      <CardHeader className="pb-2 border-b border-emerald-500/20">
        <CardTitle className="text-sm text-white flex items-center justify-between flex-wrap gap-2">
          <span className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-emerald-400" />
            Lock-In Gains Mode
            <span className="text-[10px] font-normal text-emerald-300 bg-emerald-500/15 border border-emerald-500/40 rounded px-1.5 py-0.5 capitalize">
              {data.cadence_label?.toLowerCase()} · {data.horizon_days}d horizon
            </span>
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3 space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[11px]">
          <div>
            <div className="text-[10px] uppercase text-slate-500">Portfolio value</div>
            <div className="font-mono text-base text-slate-100">{fmt$(data.portfolio_value)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-slate-500">Today's net P&amp;L</div>
            <div className={`font-mono text-base ${data.net_day_change_dollars >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
              {data.net_day_change_dollars >= 0 ? "+" : ""}{fmt$(data.net_day_change_dollars)}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-slate-500">Profit locked in</div>
            <div className="font-mono text-base text-emerald-300" data-testid="dr-lockin-total-gain">
              +{fmt$(data.total_locked_in_gain)}
            </div>
            <div className="text-[9px] text-slate-500 mt-0.5">
              across {data.n_picks} pick{data.n_picks === 1 ? "" : "s"}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-slate-500">Capital freed</div>
            <div className="font-mono text-base text-cyan-300">{fmt$(data.total_freed_capital)}</div>
            <div className="text-[9px] text-slate-500 mt-0.5">redeployable cash</div>
          </div>
        </div>

        {data.picks?.length > 0 ? (
          <div className="overflow-x-auto border border-slate-800 rounded">
            <table className="w-full text-[11px]" data-testid="dr-lockin-picks-table">
              <thead className="text-[10px] uppercase text-slate-500 bg-slate-900/50 border-b border-slate-800">
                <tr>
                  <th className="text-left py-1.5 px-3">Ticker</th>
                  <th className="text-left py-1.5 px-3">Company</th>
                  <th className="text-right py-1.5 px-3">Held</th>
                  <th className="text-right py-1.5 px-3">Gain %</th>
                  <th className="text-right py-1.5 px-3">Today</th>
                  <th className="text-right py-1.5 px-3 text-amber-300">Proj.</th>
                  <th className="text-right py-1.5 px-3">Action</th>
                  <th className="text-right py-1.5 px-3 text-emerald-300">Locked-in $</th>
                  <th className="text-left py-1.5 px-3">Signals</th>
                </tr>
              </thead>
              <tbody>
                {data.picks.map((p) => (
                  <tr key={p.ticker} className="border-b border-slate-900 hover:bg-slate-800/30"
                      data-testid={`dr-lockin-pick-${p.ticker}`}>
                    <td className="py-1 px-3 font-mono font-bold text-cyan-300">
                      <a href={`/stock?ticker=${p.ticker}`} target="_blank" rel="noopener noreferrer" className="hover:underline">
                        {p.ticker}
                      </a>
                    </td>
                    <td className="py-1 px-3 text-slate-300 truncate max-w-[160px]">{p.company_name || "—"}</td>
                    <td className="py-1 px-3 text-right font-mono text-slate-300">
                      {p.shares_held} sh
                      <span className="block text-[9px] text-slate-500">${p.market_value.toLocaleString(undefined,{maximumFractionDigits:0})}</span>
                    </td>
                    <td className={`py-1 px-3 text-right font-mono ${(p.unrealized_gain_pct || 0) >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
                      {p.unrealized_gain_pct == null ? "—" : `${p.unrealized_gain_pct >= 0 ? "+" : ""}${p.unrealized_gain_pct.toFixed(2)}%`}
                    </td>
                    <td className={`py-1 px-3 text-right font-mono ${(p.today_change_pct || 0) >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
                      {p.today_change_pct == null ? "—" : `${p.today_change_pct >= 0 ? "+" : ""}${p.today_change_pct.toFixed(2)}%`}
                    </td>
                    <td className={`py-1 px-3 text-right font-mono ${(p.projected_return_pct || 0) >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
                      {p.projected_return_pct == null ? "—" : `${p.projected_return_pct >= 0 ? "+" : ""}${p.projected_return_pct.toFixed(2)}%`}
                    </td>
                    <td className={`py-1 px-3 text-right font-mono font-bold whitespace-nowrap ${actionColor[p.action] || "text-slate-300"}`}>
                      {actionLabel[p.action] || p.action}
                      <span className="block text-[9px] text-slate-500">{p.trim_shares} sh · ${p.trim_dollars.toLocaleString(undefined,{maximumFractionDigits:0})}</span>
                    </td>
                    <td className="py-1 px-3 text-right font-mono font-bold text-emerald-300">
                      +${p.locked_in_gain.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </td>
                    <td className="py-1 px-3 text-slate-400 text-[10px]">
                      {(p.reasons || []).join(" · ") || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-900/60 border-t-2 border-emerald-500/30">
                  <td colSpan="6" className="py-1.5 px-3 text-right font-semibold text-slate-300 text-[11px]">Totals →</td>
                  <td className="py-1.5 px-3 text-right font-mono font-bold text-slate-100">
                    ${data.total_freed_capital.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </td>
                  <td className="py-1.5 px-3 text-right font-mono font-bold text-emerald-300">
                    +${data.total_locked_in_gain.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </td>
                  <td className="py-1.5 px-3" />
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          <div className="text-[11px] text-slate-400 py-4 text-center" data-testid="dr-lockin-empty">
            No fade-risk holdings detected — your winners look solid over the {data.cadence_label?.toLowerCase()} horizon. Try a longer cadence to surface medium-term reversal risk.
          </div>
        )}

        <p className="text-[10px] text-slate-600 leading-relaxed">
          Fade score combines projected return over the cadence horizon, today's % move (mean-reversion risk on big rips), and the size of the unrealized gain at risk. Trim sizes scale with signal severity: full exit when projection is sharply negative, half when mildly negative or today's move is +8%+, quarter for milder fade signals. Locked-in $ is the profit portion of the trim proceeds. Live trading involves slippage and taxes not modeled here.
        </p>
      </CardContent>
    </Card>
  );
}

function IndexETFOptionsSection({ enabled }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled) { setData(null); setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    axios.post(`${API}/options/index-multi-dte`, {}, { timeout: 240000 })
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [enabled]);

  if (!enabled) return null;
  if (loading) {
    return (
      <Card className="bg-slate-900/40 border-cyan-500/30" data-testid="dr-index-options-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-cyan-200 flex items-center gap-2">
            <Activity className="w-4 h-4 text-cyan-400 animate-pulse" /> Index ETF Options (SPY · QQQ · DIA)
          </CardTitle>
        </CardHeader>
        <CardContent className="p-3"><Skeleton className="h-24 w-full" /></CardContent>
      </Card>
    );
  }
  if (!data || !data.overlays) return null;

  const tickers = Object.keys(data.overlays);
  return (
    <Card className="bg-slate-900/40 border-cyan-500/30" data-testid="dr-index-options-card">
      <CardHeader className="pb-2 border-b border-cyan-500/15">
        <CardTitle className="text-sm text-white flex items-center justify-between flex-wrap gap-2">
          <span className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-cyan-400" />
            Index ETF Options — always liquid
            <span className="text-[10px] font-normal text-cyan-300 bg-cyan-500/10 border border-cyan-500/40 rounded px-1.5 py-0.5">
              30 / 60 / 90 DTE
            </span>
          </span>
          <span className="text-[10px] text-slate-500 hidden md:inline">
            Same-day expiries available · pick the time-decay tradeoff that fits your plan
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="dr-index-options-grid">
          {tickers.map((tk) => {
            const payload = data.overlays[tk];
            if (!payload) {
              return (
                <div key={tk} className="border border-slate-800 rounded p-3 bg-slate-950/30 opacity-60">
                  <div className="font-mono font-bold text-cyan-300 text-sm mb-1">{tk}</div>
                  <div className="text-[11px] text-slate-500">No qualifying contracts found</div>
                </div>
              );
            }
            return (
              <div key={tk} className="border border-cyan-500/25 rounded p-3 bg-slate-950/40"
                   data-testid={`dr-index-${tk}`}>
                <div className="flex items-center justify-between mb-2">
                  <a href={`/stock?ticker=${tk}`} target="_blank" rel="noopener noreferrer"
                     className="font-mono font-bold text-cyan-300 text-sm hover:underline">
                    {tk}
                  </a>
                  <span className="font-mono text-[11px] text-slate-300">${payload.current_price}</span>
                </div>
                <div className="space-y-1.5">
                  {["30d", "60d", "90d"].map((bucket) => {
                    const ov = payload.expiries?.[bucket];
                    if (!ov) {
                      return (
                        <div key={bucket} className="text-[10px] text-slate-600 italic flex items-center justify-between px-2 py-1 border border-slate-800 rounded">
                          <span className="font-mono font-bold uppercase text-slate-500">{bucket}</span>
                          <span>no qualifying contract</span>
                        </div>
                      );
                    }
                    const tooltip =
                      `${ov.expiration} · $${ov.strike} CALL\n` +
                      `Mid $${ov.mid_price} (bid $${ov.bid} / ask $${ov.ask}, spread ${ov.spread_pct}%)\n` +
                      `Δ ${ov.delta} · IV ${ov.iv_pct}%\n` +
                      `Vol ${ov.volume?.toLocaleString()} · OI ${ov.open_interest?.toLocaleString()}\n` +
                      `Leverage ~${ov.leverage_x}× · Breakeven $${ov.breakeven_price} (${ov.breakeven_move_pct >= 0 ? "+" : ""}${ov.breakeven_move_pct}%)\n` +
                      `1 contract ≈ $${ov.contract_cost?.toLocaleString()}`;
                    return (
                      <div key={bucket}
                           className="px-2 py-1.5 rounded border border-emerald-500/20 bg-emerald-500/5 cursor-help"
                           title={tooltip}
                           data-testid={`dr-index-${tk}-${bucket}`}>
                        <div className="flex items-center justify-between font-mono">
                          <span className="text-[10px] font-bold uppercase text-emerald-300">{bucket}</span>
                          <span className="text-[10px] text-slate-400">{ov.expiration} · dte {ov.dte}</span>
                        </div>
                        <div className="flex items-center justify-between mt-0.5">
                          <span className="font-mono text-[12px] font-bold text-emerald-300">${ov.strike}c</span>
                          <span className="font-mono text-[12px] text-slate-200">${ov.mid_price}</span>
                        </div>
                        <div className="text-[9px] text-slate-500 font-mono mt-0.5 flex items-center justify-between">
                          <span>Δ{ov.delta} · IV{ov.iv_pct}%</span>
                          <span>{ov.leverage_x}× lev</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        <p className="text-[10px] text-slate-600 mt-3 leading-relaxed">
          SPY / QQQ / DIA carry the deepest options liquidity in the market — same-day, weekly, and monthly expiries all qualify. We pick the highest-volume call in each DTE band with delta 0.40–0.60 (balanced sensitivity vs cost), IV ≤ 150%, and tight bid/ask spread. Hover any tile for the full contract terms.
        </p>
      </CardContent>
    </Card>
  );
}

function LossRecoverySection({ data, loading, error }) {
  if (loading) {
    return (
      <Card className="bg-slate-900/40 border-rose-500/40" data-testid="dr-recovery-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-rose-200 flex items-center gap-2">
            <TrendingDown className="w-4 h-4 text-rose-400 animate-pulse" /> Loss Recovery Mode
          </CardTitle>
        </CardHeader>
        <CardContent className="p-3 space-y-2">
          {Array(4).fill(0).map((_, i) => <Skeleton key={i} className="h-9 w-full" />)}
          <div className="text-[11px] text-slate-400 mt-2">
            Scanning every rising sub-sector for the best projected gainers and sizing them to offset your loss…
          </div>
        </CardContent>
      </Card>
    );
  }
  if (error) {
    return (
      <Card className="bg-slate-900/40 border-rose-500/40" data-testid="dr-recovery-card">
        <CardContent className="p-4 flex items-start gap-2 text-rose-300 text-[12px]">
          <AlertCircle className="w-4 h-4 mt-0.5" />
          <div><div className="font-semibold">Couldn't compute recovery picks</div><div className="text-[11px] mt-1">{error}</div></div>
        </CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const fmt$ = (v) => v == null ? "—" : `${v < 0 ? "-" : ""}$${Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  const isDownNet = data.net_day_change_dollars < 0;
  const lossLabelMap = {
    net_day_loss: "Net day loss",
    sum_of_losers: "Sum of losing positions",
    manual_override: "Manual target",
    no_loss: "No loss today",
  };

  return (
    <Card className="bg-gradient-to-br from-slate-900/60 to-rose-950/30 border-rose-500/40" data-testid="dr-recovery-card">
      <CardHeader className="pb-2 border-b border-rose-500/20">
        <CardTitle className="text-sm text-white flex items-center justify-between flex-wrap gap-2">
          <span className="flex items-center gap-2">
            <TrendingDown className="w-4 h-4 text-rose-400" />
            Loss Recovery Mode
            <span className="text-[10px] font-normal text-rose-300 bg-rose-500/15 border border-rose-500/40 rounded px-1.5 py-0.5 capitalize">
              {data.cadence_label?.toLowerCase()} · {data.horizon_days}d horizon
            </span>
            {data.covers_loss ? (
              <span className="text-[10px] font-bold text-emerald-300 bg-emerald-500/15 border border-emerald-500/40 rounded px-1.5 py-0.5">
                ✓ Fully covers
              </span>
            ) : data.loss_to_recover > 0 ? (
              <span className="text-[10px] font-bold text-amber-300 bg-amber-500/15 border border-amber-500/40 rounded px-1.5 py-0.5">
                ⚠ Partial coverage
              </span>
            ) : null}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3 space-y-3">
        {/* Top-line scorecard */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[11px]">
          <div>
            <div className="text-[10px] uppercase text-slate-500">Portfolio value</div>
            <div className="font-mono text-base text-slate-100">{fmt$(data.portfolio_value)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-slate-500">Today's net P&amp;L</div>
            <div className={`font-mono text-base ${isDownNet ? "text-rose-300" : "text-emerald-300"}`}
                 data-testid="dr-recovery-net-pnl">
              {data.net_day_change_dollars >= 0 ? "+" : ""}{fmt$(data.net_day_change_dollars)}
              <span className="text-[10px] ml-1 opacity-70">({data.net_day_change_pct}%)</span>
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-slate-500">Loss to recover</div>
            <div className="font-mono text-base text-rose-300" data-testid="dr-recovery-target">
              {fmt$(data.loss_to_recover)}
            </div>
            <div className="text-[9px] text-slate-500 mt-0.5">{lossLabelMap[data.loss_basis] || data.loss_basis}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-slate-500">Projected gain from picks</div>
            <div className="font-mono text-base text-emerald-300" data-testid="dr-recovery-total-gain">
              +{fmt$(data.total_projected_gain)}
            </div>
            <div className="text-[9px] text-slate-500 mt-0.5">
              {data.n_picks || (data.picks?.length || 0)} pick{(data.picks?.length || 0) === 1 ? "" : "s"}, max {Math.round((data.per_pick_pct_cap || 0) * 100)}% / pick
            </div>
          </div>
        </div>

        {/* Top losers being countered */}
        {(data.top_losers || []).length > 0 && (
          <div className="bg-slate-950/40 border border-rose-500/15 rounded p-2">
            <div className="text-[10px] uppercase text-slate-500 mb-1.5">Top positions dragging your book today</div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-1 text-[11px]">
              {data.top_losers.map((L) => (
                <div key={L.ticker} className="border border-rose-500/20 rounded px-2 py-1">
                  <div className="font-mono font-bold text-cyan-300">{L.ticker}</div>
                  <div className="text-rose-300 font-mono text-[10px]">{fmt$(L.day_change_dollars)} ({L.day_change_pct}%)</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Picks table */}
        {data.picks?.length > 0 ? (
          <div className="overflow-x-auto border border-slate-800 rounded">
            <table className="w-full text-[11px]" data-testid="dr-recovery-picks-table">
              <thead className="text-[10px] uppercase text-slate-500 bg-slate-900/50 border-b border-slate-800">
                <tr>
                  <th className="text-left py-1.5 px-3">Buy</th>
                  <th className="text-left py-1.5 px-3">Company</th>
                  <th className="text-right py-1.5 px-3">Today</th>
                  <th className="text-right py-1.5 px-3 text-emerald-300">Projected</th>
                  <th className="text-right py-1.5 px-3">Price</th>
                  <th className="text-right py-1.5 px-3">Shares</th>
                  <th className="text-right py-1.5 px-3">Position $</th>
                  <th className="text-right py-1.5 px-3 text-emerald-300">+$ to book</th>
                  <th className="text-left py-1.5 px-3 text-emerald-300">Option ◊</th>
                  <th className="text-left py-1.5 px-3">Sub-sector</th>
                </tr>
              </thead>
              <tbody>
                {data.picks.map((p) => (
                  <tr key={p.ticker} className="border-b border-slate-900 hover:bg-slate-800/30"
                      data-testid={`dr-recovery-pick-${p.ticker}`}>
                    <td className="py-1 px-3 font-mono font-bold text-cyan-300">
                      <a href={`/stock?ticker=${p.ticker}`} target="_blank" rel="noopener noreferrer" className="hover:underline">
                        {p.ticker}
                      </a>
                    </td>
                    <td className="py-1 px-3 text-slate-300 truncate max-w-[180px]">{p.company_name || "—"}</td>
                    <td className={`py-1 px-3 text-right font-mono ${p.today_change_pct >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
                      {p.today_change_pct == null ? "—" : `${p.today_change_pct >= 0 ? "+" : ""}${p.today_change_pct.toFixed(2)}%`}
                    </td>
                    <td className="py-1 px-3 text-right font-mono font-bold text-emerald-300">
                      +{p.projected_return_pct.toFixed(2)}%
                    </td>
                    <td className="py-1 px-3 text-right font-mono text-slate-300">${p.current_price.toFixed(2)}</td>
                    <td className="py-1 px-3 text-right font-mono text-slate-300">{p.recommended_shares}</td>
                    <td className="py-1 px-3 text-right font-mono text-slate-300">
                      ${p.recommended_dollars.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      {p.weight_pct_of_book != null && (
                        <span className="text-slate-500 text-[10px] ml-1">({p.weight_pct_of_book}%)</span>
                      )}
                    </td>
                    <td className="py-1 px-3 text-right font-mono font-bold text-emerald-300">
                      +${p.projected_dollar_gain.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </td>
                    <td className="py-1 px-3" data-testid={`dr-recovery-option-${p.ticker}`}>
                      <OptionsOverlayCell overlay={p.options_overlay} />
                    </td>
                    <td className="py-1 px-3 text-slate-400 text-[10px] truncate max-w-[160px]">{p.sub_sector || "—"}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-900/60 border-t-2 border-rose-500/30">
                  <td colSpan="6" className="py-1.5 px-3 text-right font-semibold text-slate-300 text-[11px]">Totals →</td>
                  <td className="py-1.5 px-3 text-right font-mono font-bold text-slate-100">
                    ${data.picks.reduce((s, p) => s + (p.recommended_dollars || 0), 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </td>
                  <td className="py-1.5 px-3 text-right font-mono font-bold text-emerald-300">
                    +${data.total_projected_gain.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </td>
                  <td className="py-1.5 px-3" colSpan="2" />
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          <div className="text-[11px] text-slate-400 py-4 text-center" data-testid="dr-recovery-empty">
            No qualifying gainers found at this cadence. Try a longer cadence (weekly/monthly) — daily momentum can be thin during sideways sessions.
          </div>
        )}

        <p className="text-[10px] text-slate-600 leading-relaxed">
          Projection model is the same one used for the main recommendations — daily uses intraday-velocity extrapolation, longer cadences blend analyst targets, StockPimp Predicts, and 60-day regression. New positions are deliberately diversified away from your existing book and capped at {Math.round((data.per_pick_pct_cap || 0.05) * 100)}% of portfolio value per pick. Live trading involves slippage, taxes, and risk not modeled here.
        </p>
      </CardContent>
    </Card>
  );
}

function RecommendationsSection({ recs }) {
  // Lazy-loaded "Why?" explanations keyed by `${action}-${ticker}`.
  const [openKey, setOpenKey] = useState(null);
  const [explanations, setExplanations] = useState({});
  const [loadingKey, setLoadingKey] = useState(null);

  if (!recs) return null;
  const buckets = recs.buckets || {};
  const order = ["DROP", "CUT", "KEEP", "ADD", "NEW"];

  const tot = recs.portfolio_total_value;
  const fmtDollars = (v) =>
    v === null || v === undefined ? "—" : `${v < 0 ? "-" : ""}$${Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  const fmtShares = (v) => v == null ? "—" : `${v > 0 ? "+" : ""}${Number(v).toFixed(v % 1 === 0 ? 0 : 2)} sh`;
  const actionColor = (dir) => dir === "BUY" ? "text-emerald-300" : dir === "SELL" ? "text-rose-300" : "text-slate-400";

  // Number of <td>s in a single row — drives the colSpan of the expanded panel.
  // The DROP/CUT/KEEP/ADD rows have all columns. NEW has different ones but
  // we only show "Why?" on the SELL actions where the user explicitly asked
  // for justification.
  // Column count varies by action — NEW and ADD show the options column.
  const colSpanFor = (action) => {
    if (action === "NEW") return 9;            // ticker, company, today, proj, price, action, option, sub-sector, rationale
    if (action === "ADD") return 12;           // full set + option
    return 11;                                  // DROP/CUT/KEEP — no option column
  };

  const fetchExplanation = async (action, r) => {
    const key = `${action}-${r.ticker}`;
    setOpenKey(prev => prev === key ? null : key);
    if (explanations[key]) return;  // cached
    setLoadingKey(key);
    try {
      const body = {
        ticker: r.ticker,
        action,
        cadence: recs.cadence,
        target_return: recs.target_return_pct ?? undefined,
        period_return: r.period_return_pct ?? r.change_pct_today,
        projected_return: r.projected_return_pct,
        weight_pct: r.weight_pct,
        position_pnl: r.position_pnl,
      };
      const resp = await axios.post(`${API}/daily-rebalancing/explain`, body, { timeout: 35000 });
      setExplanations(prev => ({ ...prev, [key]: resp.data }));
    } catch (e) {
      setExplanations(prev => ({
        ...prev,
        [key]: { error: e?.response?.data?.detail || e?.message || "Failed to fetch explanation" },
      }));
    }
    setLoadingKey(null);
  };

  return (
    <Card className="bg-slate-900/40 border-slate-800" data-testid="dr-recs-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-white flex items-center justify-between flex-wrap gap-2">
          <span>Recommended actions <span className="font-normal text-[10px] text-slate-400">· {recs.cadence_label || "Daily"} cadence</span></span>
          <span className="text-[10px] font-normal text-slate-500">
            DROP {recs.counts?.DROP} · CUT {recs.counts?.CUT} · KEEP {recs.counts?.KEEP} · ADD {recs.counts?.ADD} · NEW {recs.counts?.NEW}
          </span>
        </CardTitle>
        {tot && (
          <div className="text-[11px] text-slate-400 flex flex-wrap gap-x-4 gap-y-1 pt-1">
            <span>Portfolio total: <span className="text-slate-200 font-mono">${tot.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span></span>
            <span>NEW position size target: <span className="text-cyan-300 font-mono">{recs.new_buy_target_pct}% ≈ ${recs.new_buy_target_dollars?.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span></span>
            {recs.target_return_pct != null && (
              <span className="text-amber-300">
                Target return: <span className="font-mono font-bold">{recs.target_return_pct}%</span>{" "}
                <span className="opacity-70">per {recs.cadence === "daily" ? "day" : recs.cadence === "weekly" ? "week" : recs.cadence === "monthly" ? "month" : recs.cadence === "quarterly" ? "quarter" : "year"} — holdings below this are flagged to CUT/DROP</span>
              </span>
            )}
            {recs.thresholds && (
              <span className="text-slate-500">
                Thresholds: DROP&lt;{recs.thresholds.drop_pct}% · CUT≤{recs.thresholds.cut_pct}% · ADD≥{recs.thresholds.add_pct}%
              </span>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {recs.unresolved_tickers && recs.unresolved_tickers.length > 0 && (
          <div className="text-[11px] text-amber-300 bg-amber-500/10 border border-amber-500/40 rounded p-2 flex items-start gap-2"
               data-testid="dr-recs-unresolved">
            <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
            <div>
              <span className="font-semibold">Couldn't price {recs.unresolved_tickers.length} ticker{recs.unresolved_tickers.length === 1 ? "" : "s"}</span>
              {" "}— these were parsed from your upload but didn't return a live quote (delisted, foreign exchange, or unrecognized symbol). They're excluded from recommendations.
              <div className="mt-1 flex flex-wrap gap-1.5">
                {recs.unresolved_tickers.slice(0, 30).map((tk) => (
                  <span key={tk} className="font-mono text-[10px] px-1.5 py-0.5 bg-amber-500/20 rounded">{tk}</span>
                ))}
                {recs.unresolved_tickers.length > 30 && (
                  <span className="text-amber-400/70">+{recs.unresolved_tickers.length - 30} more</span>
                )}
              </div>
            </div>
          </div>
        )}
        {order.map((action) => {
          const rows = buckets[action] || [];
          if (rows.length === 0) return null;
          const cfg = ACTION_STYLE[action];
          const Icon = cfg.icon;
          const isNew = action === "NEW";
          return (
            <div key={action} className={`border rounded ${cfg.bg}`} data-testid={`dr-rec-${action}`}>
              <div className={`flex items-center justify-between px-3 py-1.5 ${cfg.color} text-[11px] font-bold`}>
                <span className="flex items-center gap-1.5"><Icon className="w-3 h-3" /> {action} · {rows.length}</span>
                <span className="font-normal text-[10px] opacity-75">{cfg.desc}</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-[11px]">
                  <thead className="text-[10px] uppercase text-slate-500 border-t border-slate-800/40">
                    <tr>
                      <th className="text-left py-1 px-3">Ticker</th>
                      <th className="text-left py-1 px-3">Company</th>
                      {!isNew && <>
                        <th className="text-right py-1 px-3">Shares</th>
                        <th className="text-right py-1 px-3">Weight</th>
                        <th className="text-right py-1 px-3">Position $</th>
                      </>}
                      <th className="text-right py-1 px-3">Today</th>
                      <th className="text-right py-1 px-3 text-amber-300">Proj.</th>
                      <th className="text-right py-1 px-3">Price</th>
                      {!isNew && <th className="text-right py-1 px-3">P&amp;L</th>}
                      <th className="text-right py-1 px-3">Action</th>
                      {(action === "NEW" || action === "ADD") && (
                        <th className="text-left py-1 px-3 text-emerald-300">Option ◊</th>
                      )}
                      {isNew && <th className="text-left py-1 px-3">Sub-sector</th>}
                      <th className="text-left py-1 px-3">Rationale</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => {
                      const rowKey = `${action}-${r.ticker}`;
                      const isOpen = openKey === rowKey;
                      const exp = explanations[rowKey];
                      const isLoadingExp = loadingKey === rowKey;
                      return (
                      <Fragment key={rowKey}>
                      <tr className="border-t border-slate-800/40">
                        <td className="py-1 px-3 font-mono font-bold text-cyan-300">
                          <a href={`/stock?ticker=${r.ticker}`} className="hover:underline">{r.ticker}</a>
                        </td>
                        <td className="py-1 px-3 text-slate-300 truncate max-w-[160px]">
                          {r.company_name || <span className="text-amber-400/70 italic">—</span>}
                        </td>
                        {!isNew && <>
                          <td className="py-1 px-3 text-right font-mono text-slate-300">{r.shares ?? "—"}</td>
                          <td className="py-1 px-3 text-right font-mono text-slate-300">
                            {r.weight_pct == null ? "—" : `${r.weight_pct.toFixed(1)}%`}
                          </td>
                          <td className="py-1 px-3 text-right font-mono text-slate-300">
                            {r.position_value == null ? "—" : `$${r.position_value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
                          </td>
                        </>}
                        <td className={`py-1 px-3 text-right font-mono ${pctClass(r.change_pct_today)}`}>{fmtPct(r.change_pct_today)}</td>
                        <td className={`py-1 px-3 text-right font-mono font-bold ${pctClass(r.projected_return_pct)}`}
                            data-testid={`dr-rec-${action}-proj-${r.ticker}`}>
                          {fmtPct(r.projected_return_pct)}
                        </td>
                        <td className="py-1 px-3 text-right font-mono text-slate-300">{fmtPrice(r.current_price)}</td>
                        {!isNew && (
                          <td className={`py-1 px-3 text-right font-mono ${pctClass(r.position_pnl)}`}>
                            {r.position_pnl == null ? "—" : `${r.position_pnl >= 0 ? "+" : ""}${fmtDollars(r.position_pnl)}`}
                          </td>
                        )}
                        <td className={`py-1 px-3 text-right font-mono whitespace-nowrap ${actionColor(r.recommended_action_direction)}`}
                            data-testid={`dr-rec-${action}-action-${r.ticker}`}>
                          {r.recommended_action_dollars == null ? "—" : (
                            <>
                              <span className="font-bold">{r.recommended_action_direction || ""}</span>{" "}
                              {fmtDollars(r.recommended_action_dollars)}
                              {r.recommended_action_shares != null && (
                                <span className="text-slate-500 ml-1 text-[10px]">({fmtShares(r.recommended_action_shares)})</span>
                              )}
                            </>
                          )}
                        </td>
                        {(action === "NEW" || action === "ADD") && (
                          <td className="py-1 px-3" data-testid={`dr-rec-${action}-option-${r.ticker}`}>
                            <OptionsOverlayCell overlay={r.options_overlay} />
                          </td>
                        )}
                        {isNew && <td className="py-1 px-3 text-slate-300 truncate max-w-[180px]">{r.sub_sector}</td>}
                        <td className="py-1 px-3 text-slate-400 text-[10px] max-w-[400px]">
                          <div className="flex items-start gap-2">
                            <span className="truncate flex-1" title={r.rationale}>{r.rationale}</span>
                            <button
                              type="button"
                              onClick={() => fetchExplanation(action, r)}
                              data-testid={`dr-rec-${action}-why-${r.ticker}`}
                              className={`shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded border flex items-center gap-0.5 ${
                                isOpen
                                  ? "border-amber-400 text-amber-200 bg-amber-500/15"
                                  : "border-slate-700 text-slate-300 hover:border-amber-500 hover:text-amber-200 hover:bg-amber-500/10"
                              }`}
                            >
                              <Lightbulb className="w-2.5 h-2.5" />
                              Why?
                              {isOpen ? <ChevronDown className="w-2.5 h-2.5" /> : <ChevronRight className="w-2.5 h-2.5" />}
                            </button>
                          </div>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="border-t border-amber-500/30 bg-slate-950/60" data-testid={`dr-rec-${action}-why-panel-${r.ticker}`}>
                          <td colSpan={colSpanFor(action)} className="py-3 px-4">
                            {isLoadingExp ? (
                              <div className="flex items-center gap-2 text-[11px] text-amber-300">
                                <RefreshCw className="w-3 h-3 animate-spin" />
                                Synthesizing reasoning for {r.ticker}…
                              </div>
                            ) : exp?.error ? (
                              <div className="text-[11px] text-rose-300">
                                Couldn't load explanation: {exp.error}
                              </div>
                            ) : exp ? (
                              <div className="grid md:grid-cols-[1.4fr_1fr] gap-4">
                                {/* Narrative */}
                                <div>
                                  <div className="flex items-center gap-1.5 text-[10px] uppercase text-amber-300 mb-1.5 font-semibold">
                                    <Lightbulb className="w-3 h-3" />
                                    Why {exp.action} {exp.ticker}?
                                    {exp.llm_used && (
                                      <span className="text-[9px] font-normal text-slate-500 border border-slate-700 rounded px-1">
                                        Claude · evidence-bound
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-[12px] text-slate-200 leading-relaxed"
                                     data-testid={`dr-explain-text-${r.ticker}`}>
                                    {exp.explanation}
                                  </p>
                                </div>
                                {/* Factors */}
                                <div>
                                  <div className="text-[10px] uppercase text-slate-500 mb-1.5 font-semibold">Supporting facts</div>
                                  <ul className="space-y-1">
                                    {(exp.factors || []).map((f, idx) => {
                                      const toneClass =
                                        f.tone === "negative" ? "text-rose-300"
                                        : f.tone === "positive" ? "text-emerald-300"
                                        : "text-slate-300";
                                      return (
                                        <li key={idx} className="text-[11px] flex items-start gap-2 leading-snug">
                                          <span className={`mt-0.5 inline-block w-1 h-1 rounded-full shrink-0 ${
                                            f.tone === "negative" ? "bg-rose-400"
                                            : f.tone === "positive" ? "bg-emerald-400"
                                            : "bg-slate-500"
                                          }`} />
                                          <span>
                                            <span className="text-slate-400">{f.label}: </span>
                                            <span className={`font-mono ${toneClass}`}>{f.value}</span>
                                          </span>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                </div>
                              </div>
                            ) : null}
                          </td>
                        </tr>
                      )}
                      </Fragment>
                    );})}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

// ────────────────────────── Page ──────────────────────────

// Cadence-aware default target returns (percent). User can edit.
const DEFAULT_TARGET_RETURN = {
  daily: 2,
  weekly: 5,
  monthly: 8,
  quarterly: 15,
  yearly: 40,
};

export default function DailyRebalancingPage() {
  const [cadence, setCadence] = useState("daily");
  const [targetReturn, setTargetReturn] = useState(DEFAULT_TARGET_RETURN.daily);
  const [targetEnabled, setTargetEnabled] = useState(true);
  const [started, setStarted] = useState(false);
  const [rising, setRising] = useState(null);
  const [candidates, setCandidates] = useState(null);
  const [selectedTicker, setSelectedTicker] = useState(null);
  const [holdings, setHoldings] = useState([]);
  const [parseInfo, setParseInfo] = useState(null);
  const [recs, setRecs] = useState(null);
  const [recsError, setRecsError] = useState(null);
  const [recsLoading, setRecsLoading] = useState(false);
  const [recsElapsed, setRecsElapsed] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  // When cadence changes, reset target return to that cadence's sensible default
  useEffect(() => {
    setTargetReturn(DEFAULT_TARGET_RETURN[cadence] ?? 5);
  }, [cadence]);

  // Elapsed-time counter while recs are computing
  useEffect(() => {
    if (!recsLoading) { setRecsElapsed(0); return; }
    const t0 = Date.now();
    const id = setInterval(() => setRecsElapsed(Math.floor((Date.now() - t0) / 1000)), 250);
    return () => clearInterval(id);
  }, [recsLoading]);

  const effectiveTarget = targetEnabled && Number.isFinite(Number(targetReturn))
    ? Number(targetReturn) : null;

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const targetQS = effectiveTarget != null ? `&target_return=${effectiveTarget}` : "";
      const [r1, r2] = await Promise.allSettled([
        axios.get(`${API}/daily-rebalancing/rising-sectors?cadence=${cadence}`, { timeout: 90000 }),
        axios.get(`${API}/daily-rebalancing/candidates?top_n_per_etf=3&max_subsectors=10&cadence=${cadence}${targetQS}`, { timeout: 180000 }),
      ]);
      if (r1.status === "fulfilled") setRising(r1.value.data);
      if (r2.status === "fulfilled") {
        setCandidates(r2.value.data);
        const top = r2.value.data?.candidates?.[0];
        if (top) setSelectedTicker(top.ticker);
      }
    } catch (e) {
      console.warn("daily-rebalancing load failed:", e?.message);
    }
    setRefreshing(false);
  }, [cadence, effectiveTarget]);

  // Reload rising/candidates whenever cadence or target changes — but ONLY
  // after the user has clicked Start. This lets them pick preferences and
  // upload a portfolio without firing off any network calls.
  useEffect(() => {
    if (!started) return;
    load();
    // Invalidate prior recommendations since they belong to the old cadence/target
    setRecs(null);
    setRecsError(null);
  }, [load, started]);

  const handleStart = () => {
    setStarted(true);
    // load() runs via the effect above once `started` flips to true
  };

  const handleRestart = () => {
    setStarted(false);
    setRising(null);
    setCandidates(null);
    setSelectedTicker(null);
    setRecs(null);
    setRecsError(null);
  };

  // Wraps `getRecs` so that clicking "Get recommendations" before the user
  // has hit Start also kicks off the rising-sectors / candidates / backtest
  // pulls (they're cheap and useful context next to the recs).
  const handleRecommendClick = () => {
    if (!started) setStarted(true);
    getRecs();
  };

  // ── Loss Recovery Mode ─────────────────────────────────────────────────
  const [recovery, setRecovery] = useState(null);
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [recoveryError, setRecoveryError] = useState(null);

  // ── Lock-In Gains Mode (flip-side) ─────────────────────────────────────
  const [lockIn, setLockIn] = useState(null);
  const [lockInLoading, setLockInLoading] = useState(false);
  const [lockInError, setLockInError] = useState(null);

  // Quick portfolio loss preview computed client-side from parsed holdings —
  // lets us auto-suggest Loss Recovery before any network call.
  // Includes the reconciliation Δ (statement vs parsed) as an alternative drag
  // source — when the broker shows the book down vs what we parsed, the user
  // explicitly asked that delta to be treated as recoverable loss.
  const portfolioLossPreview = (() => {
    if (!holdings?.length) return null;
    let net = 0, losers = 0, nLosers = 0;
    for (const h of holdings) {
      let d = h._statement_day_chg_dollar;
      if (d == null && h.market_value && h.change_pct_today != null) {
        d = (h.market_value * h.change_pct_today) / 100;
      }
      if (d == null) continue;
      net += d;
      if (d < 0) { losers += d; nLosers += 1; }
    }
    const reconDiff = parseInfo?.reconciliation?.difference ?? null;
    // Effective drag = max magnitude of: net loss, sum of losers, recon Δ (when negative)
    const candidates = [];
    if (net < 0) candidates.push({ source: "net_day_loss", value: net });
    if (losers < 0) candidates.push({ source: "sum_of_losers", value: losers });
    if (reconDiff != null && reconDiff < -5) {
      candidates.push({ source: "statement_diff", value: reconDiff });
    }
    candidates.sort((a, b) => Math.abs(b.value) - Math.abs(a.value));
    const primary = candidates[0] || null;
    return {
      net: Math.round(net * 100) / 100,
      losers: Math.round(losers * 100) / 100,
      nLosers,
      reconDiff,
      drag: primary ? Math.round(Math.abs(primary.value) * 100) / 100 : 0,
      dragSource: primary?.source || null,
    };
  })();

  const getRecoveryPicks = async () => {
    if (!holdings.length) {
      setRecoveryError("Upload a portfolio first.");
      return;
    }
    if (!started) setStarted(true);
    setRecoveryLoading(true);
    setRecoveryError(null);
    setRecovery(null);
    try {
      // If the drag source is the reconciliation Δ (broker shows book down
      // vs sum of parsed rows), pass it as target_dollars so the engine
      // sizes picks to cover *that* amount specifically. Otherwise let the
      // server auto-detect from per-row day_change.
      const body = { portfolio: holdings, cadence, max_picks: 8 };
      if (portfolioLossPreview?.dragSource === "statement_diff" && portfolioLossPreview.drag > 0) {
        body.target_dollars = portfolioLossPreview.drag;
      }
      const r = await axios.post(`${API}/daily-rebalancing/loss-recovery`, body, { timeout: 180000 });
      setRecovery(r.data);
      setTimeout(() => {
        document.querySelector('[data-testid="dr-recovery-card"]')
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    } catch (e) {
      setRecoveryError(e?.response?.data?.detail || e?.message || "Failed to compute recovery picks");
    }
    setRecoveryLoading(false);
  };

  const getLockInPicks = async () => {
    if (!holdings.length) {
      setLockInError("Upload a portfolio first.");
      return;
    }
    if (!started) setStarted(true);
    setLockInLoading(true);
    setLockInError(null);
    setLockIn(null);
    try {
      const r = await axios.post(`${API}/daily-rebalancing/lock-in-gains`,
        { portfolio: holdings, cadence, max_picks: 8 },
        { timeout: 180000 });
      setLockIn(r.data);
      setTimeout(() => {
        document.querySelector('[data-testid="dr-lockin-card"]')
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    } catch (e) {
      setLockInError(e?.response?.data?.detail || e?.message || "Failed to compute lock-in picks");
    }
    setLockInLoading(false);
  };

  const getRecs = async () => {
    if (holdings.length === 0) {
      setRecsError("No holdings parsed — upload a portfolio first.");
      return;
    }
    setRecsLoading(true);
    setRecsError(null);
    setRecs(null);
    try {
      const r = await axios.post(`${API}/daily-rebalancing/recommend`,
        { portfolio: holdings, cadence, target_return: effectiveTarget },
        { timeout: 180000 });
      setRecs(r.data);
      setTimeout(() => {
        document.querySelector('[data-testid="dr-recs-card"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Failed to generate recommendations";
      setRecsError(msg);
      console.warn("recommend failed:", msg);
    }
    setRecsLoading(false);
  };

  // Context blob for the AI chat — current uploaded portfolio + everything
  // the page has computed (recs, rising sub-sectors, candidates, recovery,
  // lock-in). Trimmed so the LLM prompt stays fast.
  const chatContext = useMemo(() => ({
    portfolio: {
      cadence,
      target_return_pct: targetEnabled ? Number(targetReturn) || null : null,
      n_holdings: holdings?.length || 0,
      total_value: parseInfo?.total_value ?? null,
      holdings: (holdings || []).slice(0, 150).map(h => ({
        ticker: h.ticker,
        market_value: h.market_value ?? null,
        change_pct_today: h.change_pct_today ?? null,
        shares: h.shares ?? null,
      })),
    },
    rebalancing: {
      day_loss_preview: portfolioLossPreview ? {
        net_day_change_dollars: portfolioLossPreview.net,
        sum_of_losers_dollars: portfolioLossPreview.losers,
        n_losing_positions: portfolioLossPreview.nLosers,
      } : null,
      recommendations: recs ? {
        cadence: recs.cadence,
        actions: (recs.recommendations || recs.actions || []).slice(0, 100).map(a => ({
          ticker: a.ticker, action: a.action, reason: (a.reason || "").slice(0, 140),
          projected_return_pct: a.projected_return_pct ?? a.projected_return ?? null,
        })),
      } : null,
      rising_sub_sectors: (rising?.rising || rising?.sub_sectors || []).slice(0, 8).map(s => ({
        name: s.name || s.sub_sector, etf: s.etf, ret_pct: s.ret ?? s.return_pct ?? s.momentum ?? null,
      })),
      top_candidates: (candidates?.candidates || []).slice(0, 10).map(c => ({
        ticker: c.ticker, sub_sector: c.sub_sector || c.subsector || null,
        momentum_pct: c.momentum ?? c.ret ?? null, projected_return_pct: c.projected_return_pct ?? null,
      })),
      loss_recovery_picks: (recovery?.picks || []).slice(0, 8).map(p => ({
        ticker: p.ticker, projected_return_pct: p.projected_return_pct ?? p.projected_return ?? null,
      })),
      lock_in_gains_flags: (lockIn?.picks || lockIn?.holdings || []).slice(0, 8).map(p => ({
        ticker: p.ticker, reason: (p.reason || "").slice(0, 100),
      })),
    },
  }), [cadence, targetEnabled, targetReturn, holdings, parseInfo, recs, rising, candidates, recovery, lockIn]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="border-b border-slate-800 bg-slate-900/40 px-4 lg:px-8 py-4">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2" data-testid="dr-title">
              <RefreshCw className="w-6 h-6 text-cyan-400" />
              Rebalancing Engine
            </h1>
            <p className="text-xs text-slate-400 mt-1 max-w-3xl">
              Rotates into the strongest sub-sectors over your chosen rebalance window and picks the top-momentum holdings from each ETF.
              Compares the strategy to SPY / DIA / QQQ buy-and-hold over 5 years before recommending action.
            </p>
          </div>
          <button
            onClick={started ? load : handleStart}
            disabled={refreshing}
            data-testid={started ? "dr-refresh-btn" : "dr-start-btn"}
            className={`text-xs px-4 py-2 rounded font-semibold border flex items-center gap-1.5 disabled:opacity-50 transition-colors ${
              started
                ? "border-slate-700 hover:bg-slate-800 text-slate-200"
                : "border-cyan-400 bg-cyan-500/20 text-cyan-100 hover:bg-cyan-500/30 shadow-[0_0_12px_rgba(34,211,238,0.25)]"
            }`}
          >
            {started ? (
              <>
                <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} /> Refresh
              </>
            ) : (
              <>
                <Activity className="w-3 h-3" /> Start rebalancing
              </>
            )}
          </button>
        </div>

        {/* Cadence selector — drives every section below it */}
        <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="dr-cadence-selector">
          <span className="text-[10px] uppercase text-slate-500 mr-1">Rebalance cadence</span>
          {CADENCES.map(c => (
            <button
              key={c.key}
              onClick={() => setCadence(c.key)}
              disabled={refreshing}
              data-testid={`dr-cadence-${c.key}`}
              className={`px-3 py-1.5 rounded border text-[11px] font-semibold transition-colors disabled:opacity-50 ${
                cadence === c.key
                  ? "bg-cyan-600/30 border-cyan-500 text-white"
                  : "border-slate-700 text-slate-400 hover:text-white hover:border-slate-500"
              }`}
            >
              {c.label}
            </button>
          ))}

          {/* Target return picker */}
          <div className="flex items-center gap-1.5 ml-3 pl-3 border-l border-slate-700"
               data-testid="dr-target-return-control">
            <label className="text-[10px] uppercase text-slate-500 flex items-center gap-1 cursor-pointer">
              <input
                type="checkbox"
                checked={targetEnabled}
                onChange={(e) => setTargetEnabled(e.target.checked)}
                data-testid="dr-target-return-toggle"
                className="accent-amber-400 w-3 h-3"
              />
              Target return
            </label>
            <input
              type="number"
              step="0.1"
              min="-50"
              max="500"
              value={targetReturn}
              onChange={(e) => setTargetReturn(e.target.value === "" ? "" : Number(e.target.value))}
              disabled={!targetEnabled || refreshing}
              data-testid="dr-target-return-input"
              className={`w-20 px-2 py-1 rounded border text-[11px] font-mono font-semibold bg-slate-900 ${
                targetEnabled
                  ? "border-amber-500/60 text-amber-200 focus:border-amber-400"
                  : "border-slate-800 text-slate-500"
              } disabled:opacity-50 outline-none`}
              placeholder={String(DEFAULT_TARGET_RETURN[cadence] ?? "")}
            />
            <span className={`text-[11px] font-bold ${targetEnabled ? "text-amber-300" : "text-slate-500"}`}>%</span>
            <span className="text-[10px] text-slate-500 hidden lg:inline ml-1">
              {targetEnabled
                ? `per ${cadence === "daily" ? "day" : cadence === "weekly" ? "week" : cadence === "monthly" ? "month" : cadence === "quarterly" ? "quarter" : "year"}`
                : "off"}
            </span>
          </div>

          <span className="ml-auto text-[10px] text-slate-500 hidden md:inline">
            {targetEnabled
              ? "Only stocks projected to meet your target are shown — and existing holdings below target will be flagged to CUT."
              : "Each cadence uses its own rising-period filter, momentum window, and DROP/CUT/ADD thresholds."}
          </span>
        </div>
      </div>

      <div className="p-4 lg:p-6 space-y-4 max-w-[1600px] mx-auto">
        {/* AI Chat — same rules as other pages (citations, on-page data) */}
        <AIAnalysisChat
          mode="portfolio"
          title="Rebalancing Portfolio — AI Chat"
          tickerName="Rebalancing Portfolio"
          extraContext={chatContext}
        />

        {!started ? (
          <Card className="bg-slate-900/40 border-cyan-500/30" data-testid="dr-prestart-card">
            <CardContent className="p-6 flex flex-col md:flex-row items-start md:items-center gap-4">
              <div className="flex-1">
                <h2 className="text-base font-semibold text-cyan-100 flex items-center gap-2 mb-1">
                  <Activity className="w-4 h-4 text-cyan-400" />
                  Ready when you are
                </h2>
                <p className="text-[12px] text-slate-300 leading-relaxed">
                  Pick your <span className="text-cyan-300 font-semibold">rebalance cadence</span> and{" "}
                  <span className="text-amber-300 font-semibold">target return</span> above,
                  optionally upload your portfolio below, then click{" "}
                  <span className="text-cyan-200 font-semibold">Start rebalancing</span> to pull
                  rising sub-sectors, momentum candidates, the 5-year backtest, and your CUT/DROP/ADD recommendations.
                </p>
                <p className="text-[11px] text-slate-500 mt-2">
                  No network calls fire until you click Start — so you can change your mind freely.
                </p>
              </div>
              <button
                onClick={handleStart}
                data-testid="dr-prestart-start-btn"
                className="text-sm px-5 py-2.5 rounded font-bold border border-cyan-400 bg-cyan-500/20 text-cyan-100 hover:bg-cyan-500/30 shadow-[0_0_14px_rgba(34,211,238,0.3)] flex items-center gap-2"
              >
                <Activity className="w-4 h-4" /> Start rebalancing
              </button>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <RisingSubSectorsSection data={rising} cadence={cadence} />
              <CandidatesSection data={candidates} onSelect={setSelectedTicker} selectedTicker={selectedTicker} />
            </div>

            {selectedTicker && <UpsideSection ticker={selectedTicker} />}

            <BacktestSection cadence={cadence} enabled={started} />

            <IndexETFOptionsSection enabled={started} />
          </>
        )}

        <PortfolioUploadSection
          onParsed={(h, info) => { setHoldings(h); setParseInfo(info); setRecs(null); setRecsError(null); setRecovery(null); setRecoveryError(null); setLockIn(null); setLockInError(null); }}
          onRecommend={handleRecommendClick}
          onRecoveryPicks={getRecoveryPicks}
          onLockInGains={getLockInPicks}
          holdings={holdings}
          parseInfo={parseInfo}
          recsLoading={recsLoading}
          recsElapsed={recsElapsed}
          recoveryLoading={recoveryLoading}
          lockInLoading={lockInLoading}
          lossPreview={portfolioLossPreview}
        />

        {/* Per-holding multi-window performance + weighted benchmark chart */}
        {holdings.length > 0 && (
          <>
            <EarlyWarningsCard portfolio={holdings} compact />
            <PortfolioReturnsTable holdings={holdings} />
            <PortfolioBenchmarkChart holdings={holdings} />
          </>
        )}

        {started && recsLoading && <RecsLoadingSkeleton elapsed={recsElapsed} />}
        {started && recsError && !recsLoading && (
          <Card className="bg-slate-900/40 border-rose-500/40" data-testid="dr-recs-error">
            <CardContent className="p-4 flex items-start gap-2 text-rose-300 text-[12px]">
              <AlertCircle className="w-4 h-4 mt-0.5" />
              <div>
                <div className="font-semibold">Couldn't generate recommendations</div>
                <div className="text-[11px] text-rose-200/80 mt-1">{recsError}</div>
              </div>
            </CardContent>
          </Card>
        )}
        {started && recs && !recsLoading && <RecommendationsSection recs={recs} />}

        {/* Loss Recovery — independent panel, can be triggered without main recs */}
        {(recovery || recoveryLoading || recoveryError) && (
          <LossRecoverySection data={recovery} loading={recoveryLoading} error={recoveryError} />
        )}

        {/* Lock-In Gains — flip-side panel */}
        {(lockIn || lockInLoading || lockInError) && (
          <LockInGainsSection data={lockIn} loading={lockInLoading} error={lockInError} />
        )}

        {started && (
          <div className="flex justify-end">
            <button
              onClick={handleRestart}
              data-testid="dr-restart-btn"
              className="text-[10px] px-2 py-1 rounded border border-slate-700 text-slate-400 hover:text-slate-200 hover:border-slate-500"
            >
              ← Back to setup
            </button>
          </div>
        )}

        <p className="text-[10px] text-slate-600 text-center pt-2 pb-6">
          ⚠️ Informational only — not investment advice. Backtest is a top-down approximation;
          live trading involves slippage, taxes, and risk not modeled here.
        </p>
      </div>
    </div>
  );
}
